import cx from 'clsx';
import React, { type KeyboardEvent, useEffect, useRef } from 'react';
import { type Control, type FieldError, useController } from 'react-hook-form';
import { type PlacesType, Tooltip } from 'react-tooltip';

import { ReactComponent as InfoIcon } from '@assets/svg/info.svg';

import useTranslation from '@hooks/useTranslation';

import style from './style.module.scss';

interface ICommonProps {
  id?: string;
  required?: boolean;
  rows?: number;
  disabled?: boolean;
  label?: string;
  type?: string;
  autoHeight?: boolean;
  rightElement?: React.ReactNode;
  tooltipContent?: string;
  tooltipPlace?: PlacesType;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
}

interface IUncontrolledProps extends ICommonProps {
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error?: FieldError;
}

interface IControlledProps extends ICommonProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IUncontrolledProps> = ({
  label,
  value,
  onChange,
  id,
  error,
  required = false,
  rows = 3,
  disabled = false,
  type,
  autoHeight = false,
  rightElement = null,
  tooltipContent,
  tooltipPlace,
  onKeyDown,
}) => {
  const { t } = useTranslation();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textareaRef.current && autoHeight) {
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`;
    }
  }, [value]);

  return (
    <div>
      {label && (
        <div className={style.inputHeader}>
          <label className={cx(style.label, { [style.required]: required })} htmlFor={id}>
            {label}
          </label>
          {tooltipContent && (
            <div id={`input-details-${id}-tooltip`} className={style.inputDetailsWrapper}>
              <InfoIcon className={style.inputDetailsIcon} />
              <Tooltip
                anchorSelect={`#input-details-${id}-tooltip`}
                content={tooltipContent}
                place={tooltipPlace}
                className={style.inputDetails}
              />
            </div>
          )}
        </div>
      )}
      <div className={style.textareaWrapper}>
        <textarea
          ref={textareaRef}
          className={cx(style.textArea, {
            [style.rightIndent]: !!rightElement,
            [style.textAreaFilledField]: !!value,
            [style.textAreaErrorField]: !!error,
          })}
          value={value}
          onChange={onChange}
          id={id}
          rows={rows}
          disabled={disabled}
          onKeyDown={onKeyDown}
        />
        {rightElement}
      </div>
      {type !== 'woHelperText' && <div className={style.helperText}>{error && t(error?.message)}</div>}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({
  id,
  label,
  control,
  name,
  required = false,
  rows = 3,
  disabled = false,
  autoHeight,
  type,
  tooltipContent,
  tooltipPlace,
  onKeyDown,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
    disabled,
  });
  return (
    <Component
      id={id || name}
      value={value}
      onChange={onChange}
      error={error}
      label={label}
      disabled={disabled}
      required={required}
      rows={rows}
      type={type}
      autoHeight={autoHeight}
      onKeyDown={onKeyDown}
      tooltipContent={tooltipContent}
      tooltipPlace={tooltipPlace}
    />
  );
};

const TextArea: React.FC<IControlledProps | IUncontrolledProps> = (props) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }
  return <Component {...props} />;
};

export default TextArea;
